/**
 * Expiring Contracts Page Component
 * US-CONT-005: View Expiring Contracts Alert
 * Main page for viewing and managing expiring contracts
 */

import { FileText, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { useContractFilters, useContractQueryParams } from '../hooks/useContractFilters';
import { useExpiringContracts } from '../hooks/useExpiringContracts';
import { useContractActions } from '../hooks/useContractActions';
import { ExpiringContractsFilters } from './ExpiringContractsFilters';
import { ExpiringContractsTable } from './ExpiringContractsTable';
import { ExpiringContractsEmptyState } from './ExpiringContractsEmptyState';

export function ExpiringContractsPage() {
  const {
    filterState,
    hasActiveFilters,
    updateFilters,
    resetFilters,
    toggleSortDirection,
    gotoPage
  } = useContractFilters();

  const queryParams = useContractQueryParams(filterState);
  const { data, isLoading, error, refetch } = useExpiringContracts(queryParams);
  const { handleAction } = useContractActions();

  function handleRefresh() {
    refetch();
  }

  return (
    <div className="flex-1 space-y-6 p-8 pt-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Expiring Contracts</h2>
          <p className="text-muted-foreground">
            Monitor and manage contracts that are expiring soon
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <ExpiringContractsFilters
        filters={filterState}
        onFiltersChange={updateFilters}
        onReset={resetFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Error State */}
      {error && (
        <Card className="p-6 border-destructive">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-destructive" />
            <div>
              <h3 className="font-medium">Failed to load contracts</h3>
              <p className="text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="ml-auto">
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Results Summary */}
      {!error && data && data.totalElements > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <p>
            Showing <span className="font-medium">{data.content.length}</span> of{' '}
            <span className="font-medium">{data.totalElements}</span> contracts
          </p>
          {!isLoading && (
            <p>
              Page {data.currentPage + 1} of {data.totalPages}
            </p>
          )}
        </div>
      )}

      {/* Table */}
      {!error && (
        <>
          {!isLoading && data && data.content.length === 0 ? (
            <ExpiringContractsEmptyState
              hasActiveFilters={hasActiveFilters}
              onReset={resetFilters}
            />
          ) : (
            <ExpiringContractsTable
              contracts={data?.content || []}
              isLoading={isLoading}
              sort={filterState}
              onSortChange={(sort) => toggleSortDirection(sort.field)}
              onAction={handleAction}
            />
          )}
        </>
      )}

      {/* Pagination */}
      {!error && data && data.totalPages > 1 && (
        <PaginationControls
          currentPage={data.currentPage}
          totalPages={data.totalPages}
          totalItems={data.totalElements}
          pageSize={data.pageSize}
          itemName="contracts"
          onPageChange={gotoPage}
        />
      )}
    </div>
  );
}
